/**
 * Geometry prepass — fills the G-buffer that nearly every other pass reads.
 *
 *   depthTexture : hardware depth (24 bit), main camera projection
 *   gNormal      : rgb = view-space normal, a = roughness + (metal ? 2 : 0)
 *   gData        : rg  = motion vector (UV delta, jitter free)
 *                  b   = view-space distance in metres (always positive)
 *                  a   = flags: 0 = world, 1 = viewmodel
 *
 * The pass swaps every visible opaque mesh onto a per-mesh prepass material so
 * each draw can carry its own previous-frame model matrix (per-object motion
 * vectors) without touching anyone else's material.
 *
 * owner: render
 */
import * as THREE from 'three';
import { makeRT } from './Common.js';

const PREPASS_VERT = /* glsl */`
#include <common>
#include <batching_pars_vertex>
#include <skinning_pars_vertex>

uniform mat4 uPrevModelMatrix;
uniform mat4 uCurViewProj;
uniform mat4 uPrevViewProj;

varying vec3 vNrm;
varying vec4 vCurClip;
varying vec4 vPrevClip;
varying float vViewZ;
#ifdef PF_CUTOUT
uniform mat3 uMapTransform;
varying vec2 vCutUv;
#endif

void main() {
  #include <batching_vertex>
  #include <beginnormal_vertex>
  #include <skinbase_vertex>
  #include <skinnormal_vertex>
  #include <defaultnormal_vertex>
  #include <begin_vertex>
  #include <skinning_vertex>
  #include <project_vertex>

  vNrm = transformedNormal;
  vViewZ = -mvPosition.z;

  vec4 objPos = vec4(transformed, 1.0);
  #ifdef USE_BATCHING
    objPos = batchingMatrix * objPos;
  #endif
  #ifdef USE_INSTANCING
    objPos = instanceMatrix * objPos;
  #endif
  vCurClip = uCurViewProj * (modelMatrix * objPos);
  vPrevClip = uPrevViewProj * (uPrevModelMatrix * objPos);

  #ifdef PF_CUTOUT
    vCutUv = (uMapTransform * vec3(uv, 1.0)).xy;
  #endif
}
`;

const PREPASS_FRAG = /* glsl */`
precision highp float;
layout(location = 0) out vec4 gNormalOut;
layout(location = 1) out vec4 gDataOut;

uniform float uPacked;   // roughness + (metal ? 2 : 0)
uniform float uFlags;

varying vec3 vNrm;
varying vec4 vCurClip;
varying vec4 vPrevClip;
varying float vViewZ;
#ifdef PF_CUTOUT
uniform sampler2D uCutMap;
uniform float uAlphaTest;
varying vec2 vCutUv;
#endif

void main() {
  #ifdef PF_CUTOUT
    if (texture2D(uCutMap, vCutUv).a < uAlphaTest) discard;
  #endif

  vec3 n = normalize(vNrm);
  if (!gl_FrontFacing) n = -n;

  vec2 cur = vCurClip.xy / max(1e-5, abs(vCurClip.w)) * sign(vCurClip.w);
  vec2 prv = vPrevClip.xy / max(1e-5, abs(vPrevClip.w)) * sign(vPrevClip.w);
  vec2 vel = (cur - prv) * 0.5;

  gNormalOut = vec4(n, uPacked);
  gDataOut = vec4(vel, max(vViewZ, 1e-4), uFlags);
}
`;

export class Prepass {
  constructor() {
    this.rt = null;
    this.width = 1;
    this.height = 1;
    this._mats = new Map();      // mesh.uuid -> material | material[]
    this._prevMatrices = new Map(); // mesh.uuid -> Matrix4
    this._swap = [];
    this._hidden = [];
    this._tmp = new THREE.Matrix4();
    this._seen = new Set();
  }

  setSize(w, h) {
    if (this.rt && this.width === w && this.height === h) return;
    this.width = w; this.height = h;
    if (this.rt) this.rt.dispose();
    const rt = makeRT(w, h, {
      count: 2,
      depthBuffer: true,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      name: 'postfx.gbuffer',
    });
    const depth = new THREE.DepthTexture(w, h);
    depth.type = THREE.UnsignedIntType;
    depth.format = THREE.DepthFormat;
    depth.minFilter = THREE.NearestFilter;
    depth.magFilter = THREE.NearestFilter;
    rt.depthTexture = depth;
    rt.textures[0].name = 'postfx.gNormal';
    rt.textures[1].name = 'postfx.gData';
    this.rt = rt;
  }

  get normalTexture() { return this.rt.textures[0]; }
  get dataTexture() { return this.rt.textures[1]; }
  get depthTexture() { return this.rt.depthTexture; }

  _materialFor(mesh, src, index) {
    const key = index === undefined ? mesh.uuid : `${mesh.uuid}:${index}`;
    let m = this._mats.get(key);
    if (!m) {
      const cutout = !!(src && src.alphaTest > 0 && (src.map || src.alphaMap));
      m = new THREE.ShaderMaterial({
        name: 'postfx.prepass',
        glslVersion: THREE.GLSL3,
        vertexShader: PREPASS_VERT,
        fragmentShader: PREPASS_FRAG,
        defines: cutout ? { PF_CUTOUT: '' } : {},
        uniforms: {
          uPrevModelMatrix: { value: new THREE.Matrix4() },
          uCurViewProj: { value: new THREE.Matrix4() },
          uPrevViewProj: { value: new THREE.Matrix4() },
          uPacked: { value: 0.9 },
          uFlags: { value: 0 },
          uCutMap: { value: cutout ? (src.map || src.alphaMap) : null },
          uAlphaTest: { value: src ? src.alphaTest : 0.5 },
          uMapTransform: { value: new THREE.Matrix3() },
        },
        side: src ? src.side : THREE.FrontSide,
        fog: false,
        toneMapped: false,
      });
      this._mats.set(key, m);
    }
    // keep in sync with the source material each frame (materials may be tuned live)
    if (src) {
      const rough = Math.min(0.999, Math.max(0.02, src.roughness !== undefined ? src.roughness : 0.9));
      const metal = src.metalness !== undefined ? src.metalness : 0.0;
      m.uniforms.uPacked.value = rough + (metal > 0.5 ? 2.0 : 0.0);
      m.side = src.side;
      if (m.defines.PF_CUTOUT !== undefined) {
        const tex = src.map || src.alphaMap;
        if (tex) {
          if (tex.matrixAutoUpdate) tex.updateMatrix();
          m.uniforms.uCutMap.value = tex;
          m.uniforms.uMapTransform.value.copy(tex.matrix);
        }
        m.uniforms.uAlphaTest.value = src.alphaTest;
      }
    }
    return m;
  }

  /**
   * @param {THREE.WebGLRenderer} renderer
   * @param {THREE.Scene} scene
   * @param {THREE.Camera} camera
   * @param {{ curViewProj: THREE.Matrix4, prevViewProj: THREE.Matrix4, flags: number,
   *           clear: boolean, onMaterial?: (m: THREE.Material) => void }} opt
   */
  render(renderer, scene, camera, opt) {
    const rt = this.rt;
    const swap = this._swap; swap.length = 0;
    const hidden = this._hidden; hidden.length = 0;
    const seen = this._seen; seen.clear();

    scene.traverseVisible((o) => {
      if (o.isMesh || o.isSkinnedMesh || o.isInstancedMesh) {
        const src = Array.isArray(o.material) ? o.material[0] : o.material;
        if (!src) { hidden.push(o); return; }
        if (opt.onMaterial) {
          if (Array.isArray(o.material)) o.material.forEach(opt.onMaterial);
          else opt.onMaterial(o.material);
        }
        if (src.transparent === true || src.visible === false
            || o.userData.postfxSkipPrepass || o.userData.aaaHelper) {
          hidden.push(o);
          return;
        }
        swap.push(o);
        seen.add(o.uuid);
      } else if (o.isPoints || o.isLine || o.isSprite) {
        hidden.push(o);
      }
    });

    // swap in the prepass materials
    for (let i = 0; i < swap.length; i++) {
      const o = swap[i];
      o.userData.__pfMat = o.material;
      let prev = this._prevMatrices.get(o.uuid);
      if (!prev) { prev = new THREE.Matrix4().copy(o.matrixWorld); this._prevMatrices.set(o.uuid, prev); }
      if (Array.isArray(o.material)) {
        const arr = o.material.map((src, idx) => {
          const m = this._materialFor(o, src, idx);
          m.uniforms.uPrevModelMatrix.value.copy(prev);
          m.uniforms.uCurViewProj.value.copy(opt.curViewProj);
          m.uniforms.uPrevViewProj.value.copy(opt.prevViewProj);
          m.uniforms.uFlags.value = opt.flags;
          return m;
        });
        o.material = arr;
      } else {
        const m = this._materialFor(o, o.userData.__pfMat);
        m.uniforms.uPrevModelMatrix.value.copy(prev);
        m.uniforms.uCurViewProj.value.copy(opt.curViewProj);
        m.uniforms.uPrevViewProj.value.copy(opt.prevViewProj);
        m.uniforms.uFlags.value = opt.flags;
        o.material = m;
      }
    }
    for (let i = 0; i < hidden.length; i++) {
      hidden[i].userData.__pfVis = hidden[i].visible;
      hidden[i].visible = false;
    }

    const bg = scene.background;
    const env = scene.environment;
    const fog = scene.fog;
    scene.background = null;
    scene.environment = null;
    scene.fog = null;

    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    const prevShadowNeeds = renderer.shadowMap.needsUpdate;
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = false;

    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(rt);
    if (opt.clear) {
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, true, false);
    } else {
      renderer.clear(false, true, false);
    }
    renderer.render(scene, camera);
    renderer.autoClear = prevAutoClear;

    renderer.shadowMap.autoUpdate = prevShadowAuto;
    renderer.shadowMap.needsUpdate = prevShadowNeeds;

    scene.background = bg;
    scene.environment = env;
    scene.fog = fog;

    // restore, and stash this frame's world matrices for next frame's velocity
    for (let i = 0; i < swap.length; i++) {
      const o = swap[i];
      o.material = o.userData.__pfMat;
      o.userData.__pfMat = null;
      this._prevMatrices.get(o.uuid).copy(o.matrixWorld);
    }
    for (let i = 0; i < hidden.length; i++) hidden[i].visible = hidden[i].userData.__pfVis;
  }

  /** Drop cached state for meshes that disappeared (keeps the maps from growing). */
  gc() {
    if (this._mats.size < 2048) return;
    for (const [k, m] of this._mats) { m.dispose(); this._mats.delete(k); }
    this._prevMatrices.clear();
  }

  dispose() {
    for (const m of this._mats.values()) m.dispose();
    this._mats.clear();
    this._prevMatrices.clear();
    if (this.rt) this.rt.dispose();
  }
}
